// Экран загрузки при входе в игру: полоса прогресса, пока скачиваются и распаковываются фоны, здания и
// карта. Один раз за запуск; если всё уже в кэше телефона — проходит за доли секунды и не мелькает.
// Потом в фоне догружается остальная графика (воины, герои, корабли, значки карты).
import { LOGIN_BG_V } from '@/lib/backgrounds';
import { removeBootSplash } from '@/lib/native';
import { criticalSteps, markBootDone, preloadImages, secondaryImages } from '@/lib/preload';
import { useGame } from '@/store/game';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

let booted = false;
/** Дольше не держим игрока: остальное догрузится по ходу игры. */
const MAX_WAIT_MS = 25_000;
/** После этого — кнопка «Играть сейчас» (медленная сеть: можно не ждать). */
const SKIP_AFTER_MS = 6_000;
/** Экран показывается, только если загрузка дольше этого (из кэша — без мелькания). */
const SHOW_AFTER_MS = 250;

export function BootLoader() {
  const { t } = useTranslation();
  const race = useGame((s) => s.user?.race);
  const [done, setDone] = useState(booted);
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState({ n: 0, total: 1 });
  const [canSkip, setCanSkip] = useState(false);

  // Стартовый фон из index.html остаётся, пока его не заменит этот экран
  useEffect(() => {
    if (!done && !visible) return;
    // Иначе между фонами мелькнёт пустой экран: картинка декодируется не сразу
    const bg = new Image();
    bg.src = `/art/login/login_mobile.webp?${LOGIN_BG_V}`;
    void bg
      .decode()
      .catch(() => undefined)
      .finally(removeBootSplash);
  }, [done, visible]);

  useEffect(() => {
    if (booted) return;
    const steps = criticalSteps(race);
    setProgress({ n: 0, total: steps.total });
    const show = window.setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    const finish = () => {
      if (booted) return;
      booted = true;
      markBootDone();
      window.clearTimeout(show);
      setDone(true);
    };
    const limit = window.setTimeout(finish, MAX_WAIT_MS);
    const skip = window.setTimeout(() => setCanSkip(true), SKIP_AFTER_MS);
    void steps
      .run(() => setProgress((p) => ({ ...p, n: Math.min(p.total, p.n + 1) })))
      .finally(() => {
        window.clearTimeout(limit);
        finish();
      });
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(limit);
      window.clearTimeout(skip);
    };
  }, []);

  // После входа — в фоне, по 2 картинки за раз, чтобы не мешать игре
  useEffect(() => {
    if (!done) return;
    const id = window.setTimeout(() => void preloadImages(secondaryImages(race), undefined, 2), 2000);
    return () => window.clearTimeout(id);
  }, [done, race]);

  if (done || !visible) return null;
  const pct = Math.round((progress.n / Math.max(1, progress.total)) * 100);
  return (
    <div
      className="theme-dark fixed inset-0 z-[90] flex flex-col items-center justify-center gap-4 bg-ink-950 bg-cover bg-center px-8"
      style={{
        backgroundImage: `linear-gradient(rgba(10,8,6,.55), rgba(10,8,6,.85)), url(/art/login/login_mobile.webp?${LOGIN_BG_V})`,
      }}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={t('loading')}
    >
      <b className="font-display text-3xl text-gold-300 drop-shadow">Kings of Seas</b>
      <div className="boot-shine h-3 w-full max-w-xs overflow-hidden rounded-full border border-gold-600 bg-ink-800">
        <div
          className="h-full bg-gradient-to-r from-gold-500 to-gold-300 transition-[width] duration-200"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-sm text-parchment-200">
        {t('game:boot.loading')} · {pct}%
      </p>
      <p className="max-w-xs text-center text-xs text-parchment-300">{t('game:boot.hint')}</p>
      {canSkip && (
        <button
          type="button"
          className="mt-2 text-sm text-gold-300 underline"
          onClick={() => {
            booted = true;
            markBootDone();
            setDone(true);
          }}
        >
          {t('game:boot.playNow')}
        </button>
      )}
    </div>
  );
}
